import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import EmailTemplateCard from '@/components/admin/EmailTemplateCard';
import EmailTemplateEditor from '@/components/admin/EmailTemplateEditor';
import SentEmailTable from '@/components/admin/SentEmailTable';
import { Mail, Edit } from 'lucide-react';
import EmailComposer from '@/components/admin/EmailComposer';
import AdminLayout from '@/components/admin/AdminLayout';

interface EmailTemplate {
  id: string;
  template_name: string;
  subject: string;
  body_html: string;
  body_text: string;
  variables: string[];
  is_active: boolean;
  category: string;
}

export default function Email() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('category', { ascending: true });
      
      if (error) throw error;
      return data as EmailTemplate[];
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async (template: EmailTemplate) => {
      const { error } = await supabase
        .from('email_templates')
        .update({
          subject: template.subject,
          body_html: template.body_html,
          body_text: template.body_text,
          is_active: template.is_active,
        })
        .eq('id', template.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({
        title: 'Template Updated',
        description: 'Email template has been updated successfully.',
      });
      setIsEditorOpen(false);
      setEditingTemplate(null);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update template: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsEditorOpen(true);
  };

  const handleSaveTemplate = (template: EmailTemplate) => {
    updateTemplateMutation.mutate(template);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Email Management</h1>
            <p className="text-muted-foreground">
              Manage email templates and view sent email history
            </p>
          </div>
        </div>

      <Tabs defaultValue="compose" className="space-y-4">
        <TabsList>
          <TabsTrigger value="compose">
            <Edit className="h-4 w-4 mr-2" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="sent">Sent Emails</TabsTrigger>
          <TabsTrigger value="templates">
            <Mail className="h-4 w-4 mr-2" />
            Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Templates</CardTitle>
              <CardDescription>
                Manage and customize your client-facing email templates
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading templates...
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {templates?.map((template) => (
                    <EmailTemplateCard
                      key={template.id}
                      template={template}
                      onEdit={handleEditTemplate}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compose" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Compose New Email</CardTitle>
              <CardDescription>
                Send a custom email to a client with variable support
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EmailComposer />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sent Emails Archive</CardTitle>
              <CardDescription>
                View all emails sent to clients
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SentEmailTable />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {isEditorOpen && editingTemplate && (
        <EmailTemplateEditor
          template={editingTemplate}
          open={isEditorOpen}
          onOpenChange={setIsEditorOpen}
          onSave={handleSaveTemplate}
        />
      )}
      </div>
    </AdminLayout>
  );
}
